import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { ArrowLeft } from "lucide-react";
import Sidebar from "./Sidebar";
import Footer from "./Footer";

const PageTitleContext = createContext({
  setTitle: () => {},
  setBack: () => {},
  setFullBleed: () => {},
});

export function usePageTitle(title) {
  const { setTitle } = useContext(PageTitleContext);
  useEffect(() => {
    setTitle(title);
    return () => setTitle("");
  }, [title, setTitle]);
}

export function usePageHeaderBack(onBack) {
  const { setBack } = useContext(PageTitleContext);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const hasBack = Boolean(onBack);
  useEffect(() => {
    setBack(hasBack ? { onBack: () => onBackRef.current?.() } : null);
    return () => setBack(null);
  }, [hasBack, setBack]);
}

export function usePageContentFullBleed(active = true) {
  const { setFullBleed } = useContext(PageTitleContext);
  useEffect(() => {
    setFullBleed(active);
    return () => setFullBleed(false);
  }, [active, setFullBleed]);
}

export default function AppShell({
  brand = "Adlywise DSP",
  brandSubtitle,
  roleLabel,
  sections,
  sidebarFooter,
  user,
  userMenuItems = [],
  children,
}) {
  const [title, setTitle] = useState("");
  const [back, setBack] = useState(null);
  const [fullBleed, setFullBleed] = useState(false);
  const value = {
    setTitle: useCallback((t) => setTitle(t ?? ""), []),
    setBack: useCallback((b) => setBack(b ?? null), []),
    setFullBleed: useCallback((b) => setFullBleed(Boolean(b)), []),
  };

  return (
    <PageTitleContext.Provider value={value}>
      <div className="flex h-screen w-full overflow-hidden bg-(--canvas) text-(--text-primary)">
        <Sidebar
          brand={brand}
          brandSubtitle={brandSubtitle}
          roleLabel={roleLabel}
          sections={sections}
          footer={sidebarFooter}
          user={user}
          userMenuItems={userMenuItems}
        />
        <main className="flex h-full min-w-0 flex-1 flex-col overflow-hidden">
          <header className="flex shrink-0 items-center justify-between gap-6 border-b border-(--border-subtle) bg-(--surface-1) px-[30px] py-4">
            <div className="flex min-w-0 items-center gap-3">
              {back && (
                <button
                  type="button"
                  onClick={back.onBack}
                  aria-label="Back"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-(--radius-md) text-(--text-secondary) hover:bg-(--surface-2) hover:text-(--text-primary)"
                >
                  <ArrowLeft size={20} strokeWidth={2} />
                </button>
              )}
              <h1 className="truncate text-[27px] font-extrabold tracking-[-0.02em] text-(--text-primary)">
                {title}
              </h1>
            </div>
          </header>
          {fullBleed ? (
            <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
          ) : (
            <div className="flex-1 overflow-y-auto px-[30px] py-7">
              {children}
            </div>
          )}
          <Footer />
        </main>
      </div>
    </PageTitleContext.Provider>
  );
}
